/**
 * The Melio Health Check — the questionnaire behind the slide-in panel.
 *
 * STATUS: DRAFT. Wording is pending the clinical lead. Until it is approved the
 * panel stays as written here and HEALTH_CHECK_STATUS stays 'draft'.
 *
 * WHAT THE QUESTIONS MAY ASK. They ask how someone's health is *managed* —
 * whether anyone reviews their bloods, whether one clinician holds the whole
 * picture, whether there is a plan — never about symptoms, conditions,
 * medication or family medical history. The answers are scored in the browser
 * to pick one of three non-clinical summaries and are NEVER sent anywhere: the
 * waitlist schema forbids clinical data pre-account (src/types/waitlist.ts,
 * "NOTHING CLINICAL LIVES HERE"), and the capture step sends only name, email
 * and consent.
 */


export type QuestionId = 'bloods' | 'family' | 'clinician' | 'followup' | 'habits' | 'plan';
export type OptionId = 'a' | 'b' | 'c';

export interface HcOption {
  id: OptionId;
  label: string;
  /** 2 = well managed, 1 = partly, 0 = not at all. Summed into a band. */
  weight: 0 | 1 | 2;
}

export interface HcQuestion {
  id: QuestionId;
  prompt: string;
  options: HcOption[];
}

export const HEALTH_CHECK_QUESTIONS: HcQuestion[] = [
  {
    id: 'bloods',
    prompt: 'When did a doctor last review a full set of your bloods with you?',
    options: [
      { id: 'a', label: 'In the last year', weight: 2 },
      { id: 'b', label: 'One to three years ago', weight: 1 },
      { id: 'c', label: 'Longer ago or never', weight: 0 },
    ],
  },
  {
    id: 'family',
    prompt: 'How well is your family history written down and used?',
    options: [
      { id: 'a', label: 'Recorded, and it shapes what I check', weight: 2 },
      { id: 'b', label: 'I know it, but nobody has written it down', weight: 1 },
      { id: 'c', label: 'Patchy, or I have never been asked', weight: 0 },
    ],
  },
  {
    id: 'clinician',
    prompt: 'Is there one clinician who knows your whole picture?',
    options: [
      { id: 'a', label: 'Yes, and we speak through the year', weight: 2 },
      { id: 'b', label: 'Sort of—a few people know parts of it', weight: 1 },
      { id: 'c', label: 'No one person has the whole picture', weight: 0 },
    ],
  },
  {
    id: 'followup',
    prompt: 'When a result or a question needs following up, who does it?',
    options: [
      { id: 'a', label: 'Someone follows it up for me', weight: 2 },
      { id: 'b', label: 'I do, when I remember', weight: 1 },
      { id: 'c', label: 'Things tend to drop', weight: 0 },
    ],
  },
  {
    id: 'habits',
    prompt: 'Over the last three months, how consistent have sleep and movement been?',
    options: [
      { id: 'a', label: 'Steady, most weeks', weight: 2 },
      { id: 'b', label: 'Up and down', weight: 1 },
      { id: 'c', label: 'Honestly, not something I track', weight: 0 },
    ],
  },
  {
    id: 'plan',
    prompt: 'Do you have a plan for what to check, and when, over the next year?',
    options: [
      { id: 'a', label: 'Yes, written down', weight: 2 },
      { id: 'b', label: 'Roughly, in my head', weight: 1 },
      { id: 'c', label: 'No plan', weight: 0 },
    ],
  },
];

export type Band = 'managed' | 'partly' | 'unmanaged';

/** Score thresholds over a maximum of 12. */
export const BAND_THRESHOLDS: { band: Band; min: number }[] = [
  { band: 'managed', min: 9 },
  { band: 'partly', min: 5 },
  { band: 'unmanaged', min: 0 },
];

/**
 * Non-clinical summaries. These describe how the person's health is being
 * looked after, not their health — no numbers, no advice, no diagnosis.
 */
export const HEALTH_CHECK_SUMMARY: Record<Band, { title: string; body: string }> = {
  managed: {
    title: 'Well held.',
    body: 'Most of the pieces are already in place. What Melio adds is continuity: one clinician who keeps the whole picture, year after year.',
  },
  partly: {
    title: 'Partly held.',
    body: 'Some of your health is looked after and some of it is waiting on you. Joining those parts up is what a Head of Health is for.',
  },
  unmanaged: {
    title: 'Mostly on you.',
    body: "Right now the joining-up is being done by you or by nobody. Taking that on is Melio's whole job.",
  },
};

export const HEALTH_CHECK_COPY = {
  panelTitle: 'Melio Health Check',
  introTitle: 'How well is your health being managed?',
  introBody: 'A few questions to understand where you are today.',
  introCta: 'Start your health check',
  next: 'Next',
  back: 'Back',
  captureTitle: 'Where to send your next steps',
  captureBody: 'We will write with a short note on where you stand and what Melio would take on. Your answers stay on this device—we only keep your name and email.',
  captureSubmit: 'Send me my next steps',
  doneNote: "We haven't opened to new members yet. When we do, we'll write to you before anyone else.",
} as const;
